import { FC, useState } from 'react';
import { dispatch } from '../../dispatch/dispatch';
import { saveOwnFloor } from './OwnFloorField';
import { actions } from '../../dispatch';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Plus, RefreshCw, X, DollarSign, Image } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PrefillProduct {
  _id: string;
  Sku: string;
  name?: string;
  imgUri?: string;
}

export interface CreateGroupModalProps {
  prefillProduct?: PrefillProduct;
  prefillProducts?: PrefillProduct[];
  token: string;
  onCreated: (gId: string, gName: string) => void;
  onClose: () => void;
}

// ─── Local ProductImage helper ────────────────────────────────────────────────

const ProductImage: FC<{ src?: string; alt?: string; size?: number }> = ({ src, alt, size = 56 }) => {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div
        style={{ width: size, height: size, minWidth: size }}
        className="flex items-center justify-center rounded-lg bg-muted border border-border"
      >
        <Image className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }
  return (
    <img
      src={src} alt={alt || 'Product'} onError={() => setErr(true)}
      style={{ width: size, height: size, minWidth: size }}
      className="rounded-lg object-cover border border-border bg-muted"
    />
  );
};

// ─── CreateGroupModal ─────────────────────────────────────────────────────────

export const CreateGroupModal: FC<CreateGroupModalProps> = ({
  prefillProduct, prefillProducts, onCreated, onClose, token
}) => {
  const [form, setForm] = useState({
    name:            prefillProduct?.name || prefillProduct?.Sku || '',
    category:        '',
    notes:           '',
    supplier:        '',
    minStockAlert:   0,
    buyingRate:      0,
    transportFees:   2,
    storageFees:     2,
    packageFees:     3,
    jumiaCommission: 15,
    fraisTraitement: 6,
    newMarketPrice:  0,
  });
  const [saving, setSaving] = useState(false);
  const [ownFloor, setOwnFloor] = useState('');   // optional: this product's own floor (MAD profit), beats every rule

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const totalCost = (form.buyingRate || 0) + (form.transportFees || 0) + (form.storageFees || 0) + (form.packageFees || 0);

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    if (!form.buyingRate || form.buyingRate <= 0) return;
    setSaving(true);
    const productIds: string[] = prefillProducts
      ? prefillProducts.map(p => p._id)
      : prefillProduct ? [prefillProduct._id] : [];

    const result = await dispatch(actions.createProductGroup, {
      ...form,
      productIds,
      imageUrl: prefillProduct?.imgUri || prefillProducts?.[0]?.imgUri || null,
    }, token);

    setSaving(false);
    if (result?._id && ownFloor.trim() !== '' && Number(ownFloor) >= 0) await saveOwnFloor(result._id, Number(ownFloor), token).catch(() => {});
    if (result?._id) onCreated(result._id, result.name);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-dropdown overflow-hidden" onClick={e => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-sm font-bold text-foreground">Create New Product Group</h2>
            {(prefillProduct || prefillProducts) && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {prefillProducts ? `${prefillProducts.length} SKUs will be added` : `Will include ${prefillProduct?.Sku}`}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors"><X className="h-4 w-4 text-muted-foreground" /></button>
        </div>

        <div className="px-5 py-4 space-y-3.5 max-h-[70vh] overflow-y-auto">
          {/* Image preview */}
          {(prefillProduct?.imgUri || prefillProducts?.[0]?.imgUri) && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border">
              <ProductImage src={prefillProduct?.imgUri || prefillProducts?.[0]?.imgUri} size={48} />
              <p className="text-xs text-muted-foreground">This image will be used for the group</p>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-xs font-semibold text-foreground">Group Name *</label>
            <input
              autoFocus
              value={form.name}
              onChange={e => set('name', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              placeholder="e.g. Massage Gun - MP1420"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-foreground">Category</label>
              <input value={form.category} onChange={e => set('category', e.target.value)}
                placeholder="e.g. Health & Beauty"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-foreground">Supplier</label>
              <input value={form.supplier} onChange={e => set('supplier', e.target.value)}
                placeholder="Supplier name"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-foreground">Min. Stock Alert</label>
            <input type="number" min={0} value={form.minStockAlert} onChange={e => set('minStockAlert', parseInt(e.target.value) || 0)}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-foreground">Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
              rows={2} placeholder="Optional notes…"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20 resize-none" />
          </div>

          {/* Fees */}
          <div className="rounded-xl border border-border bg-muted/40 p-3 space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
              <DollarSign className="h-3 w-3" /> Cost per unit (MAD) — for profit calculation
            </p>
            <div className="grid grid-cols-2 gap-2">
              {([
                ['buyingRate',    'Buying Rate *'],
                ['transportFees', 'Inbound transport'],
                ['storageFees',   'Hub delivery cost'],
                ['packageFees',   'Packaging'],
              ] as const).map(([key, label]) => (
                <div key={key} className="space-y-0.5">
                  <label className={`text-[10px] font-semibold ${key === 'buyingRate' ? 'text-red-500' : 'text-muted-foreground'}`}>{label}</label>
                  <input type="number" min={0} step={0.1} value={(form as any)[key]}
                    onChange={e => set(key, parseFloat(e.target.value) || 0)}
                    className={`w-full rounded-lg border bg-background px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring/20 ${key === 'buyingRate' && !(form as any)[key] ? 'border-red-400 focus:border-red-400' : 'border-input focus:border-ring'}`} />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-1.5 border-t border-border">
              <span className="text-xs text-muted-foreground">Total cost</span>
              <span className="text-xs font-bold text-foreground">{totalCost.toFixed(2)} MAD</span>
            </div>
            <div className="space-y-0.5 pt-1 border-t border-border">
              <label className="text-[10px] font-semibold text-muted-foreground">New Market Price (MAD)</label>
              <input type="number" min={0} step={0.01} value={form.newMarketPrice}
                onChange={e => set('newMarketPrice', parseFloat(e.target.value) || 0)}
                className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
              <p className="text-[10px] text-muted-foreground">Prix marché actuel — mis à jour automatiquement à chaque achat</p>
            </div>
            <div className="space-y-0.5 pt-1 border-t border-border">
              <label className="text-[10px] font-semibold text-muted-foreground">🛡 Own floor — minimum profit per sale (MAD, optional)</label>
              <input type="number" min={0} value={ownFloor} placeholder="empty = global rules (Jumia 25, Marjane 40)" onChange={e => setOwnFloor(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
              <p className="text-[10px] text-muted-foreground">Beats every rule, Jumia and Marjane, Auto-Pilot included.</p>
            </div>
          </div>

          {/* Jumia fees — stored but not included in profit calc */}
          <div className="rounded-xl border border-border bg-muted/40 p-3 space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
              <DollarSign className="h-3 w-3" /> Jumia Fees — stored, not auto-calculated
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-0.5">
                <label className="text-[10px] font-semibold text-muted-foreground">Commission Jumia (%)</label>
                <input type="number" min={0} max={100} step={0.1} value={form.jumiaCommission}
                  onChange={e => set('jumiaCommission', parseFloat(e.target.value) || 0)}
                  className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
              </div>
              <div className="space-y-0.5">
                <label className="text-[10px] font-semibold text-muted-foreground">Frais de traitement (DH)</label>
                <input type="number" min={0} step={0.1} value={form.fraisTraitement}
                  onChange={e => set('fraisTraitement', parseFloat(e.target.value) || 0)}
                  className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 px-5 py-4 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose} className="flex-1">Cancel</Button>
          <Button size="sm" disabled={!form.name.trim() || !form.buyingRate || form.buyingRate <= 0 || saving}
            className={cn("flex-1 bg-brand hover:bg-brand-dark text-white")}
            onClick={handleCreate}
          >
            {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
            {saving ? 'Creating…' : 'Create Group'}
          </Button>
        </div>
      </div>
    </div>
  );
};
